import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { Display, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER_ROW, FLEX_ITEM_AUTO, H_100, PADDING, SHADOW, TEXT_TRUNCATE, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { getLogConsole, getLogService } from "@cervval.js/common/src/services/log.service";
import { ValueOrFunction, anyReallyAny, clone, compare, compareFields, getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { ColumnUid, RowUid, SmartTableColumnConfig } from "@cervval.js/common/src/ui/columnConfiguration/columnConfiguration";
import { CjsDialog, DialogOptions, DialogPreConfig } from "../../../components/dialog/dialog";
import { CjsButton } from "../../button/button";
import { CjsEllipsis } from "../../ellipsis/ellipsis";
import { ShowPopupStyling, showPopup } from "../../showPopup/showPopup";
import { CjsSmartList } from "../../smartList/smartList";
import { SELECTION_TYPE } from "../selectionModel/smartTableSelectionModel";
import { CjsSmartTable, SINGLE_COLUMN_UID, SmartTableCell, SmartTableOptions } from "../smartTable";
import { SmartTableCellGetValue, SmartTableColumn, buildInitialColumnConfiguration, getColumnsFromConfiguration, updateOrderColumnConfiguration, updateVisibleColumnConfigurations } from "../smartTableColumn.tools";
import { I18nType, i18n } from "./i18n/i18n";

/** Reason when refresh smart table*/
export const columnConfiguratorSmartTableRefreshReason = "ColumnConfigurationUpdate";

/** Dispatch when column have been configured */
export type SmartTableColumnConfiguredEvent = {
    /** New column configuration */
    columnConfigs: SmartTableColumnConfig[]
}

export interface ColumnConfiguratorOptions {
    /** List of non-editable columns uids (column can't be moved to left pane) */
    nonRemovableColumnUids?: string[];

    /**
     * Disable table refresh with columnsConfiguration
     * You should manually refresh table if disabled
     */
    disableTableRefreshInConfiguratorRefresh?: boolean;

    /** The column configurator will be opened in a context menu triggered by a click on the smartTable header */
    enableContextMenu?: boolean;

    /** Default visible column uids */
    defaultVisibleColumnUids?: string[];

    /** If a uid is provided, configs are stored in the local storage (and loaded on init) */
    localStorageConfigUid?: string;

    /** 
     * Non visible columns are sorted with this comparator 
     * By default we sort by string titles, if you have html, you need to override this option
     */
    sortComparator?: <ItemData>(configurator: CjsColumnConfigurator<ItemData> | undefined, a: SmartTableColumn<ItemData>, b: SmartTableColumn<ItemData>) => number;

    /** Override get column name (default is column title) */
    getColumnName?: <ItemData>(col: SmartTableColumn<ItemData>, colHeaderElement?: HTMLTableCellElement) => AcceptedHtmlElementContent | undefined;

    /** How to handle empty columns 
     * By default empty columns are not allowed and default columns are restored
     */
    handleEmptyColumnsWithContextMenu?: boolean;
}

export const defaultColumnConfiguratorOptions: ColumnConfiguratorOptions = {
    /** By default we sort by string titles, if you have html, you need to override this option */
    sortComparator: (configurator, a, b) => {
        const aTitle = configurator?.getColumnName(a) ?? a.title;
        const bTitle = configurator?.getColumnName(b) ?? b.title;
        return compare(String(aTitle), String(bTitle));
    }
};

export interface ColumnConfiguratorInitOptions<ItemData> {

    /** Configuration of columns */
    columnConfig?: SmartTableColumnConfig[];

    /** SmartTable to configure columns */
    smartTable?: CjsSmartTable<ItemData>;

    /** If smartTable isn't given, use columns to build configuration */
    columns?: SmartTableColumn<ItemData>[];

    /** Used to override style */
    overrideCssPopup?: ShowPopupStyling;

}

/** Components that helps configure columns : interacts directly with smartTable set in constructor */
@CustomElement({
    name: "cjs-column-configurator",
    template: require("./columnConfigurator.html"),
    style: require("./columnConfigurator.scss"),
    i18n,
    elementCssClasses: [FLEX_CONTAINER_ROW, FLEX_ITEM_AUTO, W_100, PADDING.P_1, H_100]
})
export class CjsColumnConfigurator<ItemData> extends WebComponent<I18nType> {

    @TypedWebComponentOptions(defaultColumnConfiguratorOptions)
    public readonly options!: WebComponentOptions<ColumnConfiguratorOptions>;

    @TypedHtmlElements({ ensureLoadedClasses: [CjsSmartList] })
    public components!: {
        nonVisibleColumnsContainer: HTMLDivElement,
        nonVisibleColumnsLabel: HTMLDivElement,
        nonVisibleColumns: CjsSmartList<SmartTableColumn<ItemData>>,
        buttonContainerLeftRight: HTMLDivElement,
        visibleColumnsContainer: HTMLDivElement,
        visibleColumnsLabel: HTMLDivElement,
        visibleColumns: CjsSmartList<anyReallyAny>,
        moveRightBtn: CjsButton,
        moveLeftBtn: CjsButton,
        moveUpBtn: CjsButton,
        moveDownBtn: CjsButton,
        allBtn: CjsButton,
        noneBtn: CjsButton,
        moveOnTopBtn: CjsButton,
        moveAtBottomBtn: CjsButton,
        resetBtn: CjsButton
    };

    /**
     * Event dispatched when columns configuration has changed
     */
    @Event(NoBubblesEventInit)
    public columnConfigurationHasChangeEvent!: EventHandler<SmartTableColumnConfiguredEvent>;

    /** Configuration sorted by column order */
    protected _columnConfiguration: SmartTableColumnConfig[] = [];

    /** Get columnConfiguration */
    public get columnConfiguration(): SmartTableColumnConfig[] {
        return this._columnConfiguration;
    }

    /** Set columnConfiguration and store in local storage if option is active */
    public set columnConfiguration(newValue: SmartTableColumnConfig[]) {
        this._columnConfiguration = newValue;
        if (this.options.localStorageConfigUid != null) {
            // store in local storage
            try {
                localStorage.setItem(this.options.localStorageConfigUid, JSON.stringify(newValue));
            } catch (err) {
                getLogConsole().error(err);
            }
        }
    }

    /** Store all columns in configurator */
    public columns: ValueOrFunction<SmartTableColumn<ItemData>[]> = [];

    /** Store table to update columns */
    public table?: CjsSmartTable<ItemData>;

    /** Delay of tooltip display when hover button */
    public BUTTONS_TOOLTIP_DELAY = 500;// ms

    /** Saved config of configurator when using menu */
    public savedConfig: SmartTableColumnConfig[] | undefined = undefined;

    /** Store config of configurator */
    public config: ColumnConfiguratorInitOptions<ItemData>;

    /** Selection for persistence */
    protected _leftSelection: ColumnUid[] = [];
    protected _rightSelection: ColumnUid[] = [];
    protected _verticalSelection: ColumnUid[] = [];

    constructor(config: ColumnConfiguratorInitOptions<ItemData>, options?: Partial<ColumnConfiguratorOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
        this.initColumnConfigurator(config);
        this.config = config;
    }

    /** Init */
    public override init(): void {
        this._initSmartLists();
        this._initButtons();
    }

    /** Init column configurator component with smartTable / columnsConfig or columns */
    public initColumnConfigurator(config?: ColumnConfiguratorInitOptions<ItemData>): void {
        if (config == null) { return; }

        const columnsConfig: SmartTableColumnConfig[] | undefined = this.initColumnConfig(config);

        this.columnConfiguration = columnsConfig ?? [];
        if (config.smartTable && columnsConfig?.length) {
            config.smartTable.options.setWithoutRefresh({ columnsConfig });
        }

        if (this.options.enableContextMenu) {
            this._installHeaderContextMenu(config);
        }
    }

    /**
     * Init SmartTableColumnConfigs from options
     * @param config 
     * @returns SmarTable column configs
     */
    protected initColumnConfig(config: ColumnConfiguratorInitOptions<ItemData>): SmartTableColumnConfig[] | undefined {
        let columnsConfig: SmartTableColumnConfig[] | undefined = undefined;
        // Assign smartTable and columns
        if (config.smartTable) {
            this.table = config.smartTable;
            this.columns = () => this.table?.options.columns;
        }
        else if (config.columns) { this.columns = config.columns; }

        // determine config from options

        if (columnsConfig == null && config.smartTable) {
            // no config provided => load from local storage
            if (this.options.localStorageConfigUid != null) {
                const configStr = localStorage.getItem(this.options.localStorageConfigUid);
                if (configStr != null) {
                    try {
                        columnsConfig = JSON.parse(configStr);
                    } catch (err) {
                        getLogConsole().error(err);
                    }
                }
            }
        }
        if (columnsConfig == null && config.columnConfig) {
            // read from config
            columnsConfig = config.columnConfig;
        }
        if (columnsConfig == null && config.smartTable?.options.columnsConfig) {
            // read config from smartTable options
            columnsConfig = config.smartTable.options.columnsConfig;
        }

        const columns = getVOFValue(this.columns);
        if (columnsConfig == null && columns) {
            // Last try from columns
            columnsConfig = buildInitialColumnConfiguration(columns, this.options.defaultVisibleColumnUids);
        }

        return columnsConfig;
    }

    /** Install the column configurator in a popup as a context menu triggered on the smart table header */
    protected _installHeaderContextMenu(config: ColumnConfiguratorInitOptions<ItemData>): void {
        if (config.smartTable) {
            config.smartTable.components.tableHead.addEventListener("contextmenu", (event) => {
                event.preventDefault();

                // Save current config for cancel
                if (this.columnConfiguration.length) {
                    this.savedConfig = clone(this.columnConfiguration);
                } else if (this.table?.options.columnsConfig) {
                    this.savedConfig = clone(this.table?.options.columnsConfig) ?? [];
                }

                showPopup({
                    parent: document.body,
                    elementToShow: this,
                    event,
                    modifiers: [
                        {
                            name: "flip",
                            options: {
                                fallbackPlacements: ["top", "bottom"]
                            }
                        }
                    ],
                    absolutePosition: {
                        top: event.clientY,
                        left: event.clientX
                    },
                    cssPopup: {
                        cssClass: [
                            SHADOW.S10,
                            PADDING.P_2,
                            ...config.overrideCssPopup?.cssClass ?? []
                        ],
                        style: {
                            minWidth: "700px",
                            maxWidth: "800px",
                            height: "390px", // larger in order to be able to display resetBtn
                            border: "solid 2px var(--cjs-separator)",
                            borderRadius: "5px",
                            ...config.overrideCssPopup?.style
                        }
                    },
                    onPopupCancel: () => {
                        this.showEmptyDialogAndRestoreDefaultColumns.bind(this)();
                    },
                    onPopupHide: () => {
                        this.showEmptyDialogAndRestoreDefaultColumns.bind(this)();
                    }
                });
            });
        } else {
            getLogService().warn("SmartTable is undefined. Cannot install the context menu.");
        }
    }

    /** Set columns and columns configuration 
     * ! Used only in column editor, columnConfiguration can be set to [], then contextual menu cannot open.
     */
    public setColumnConfiguration(columnConfigs: SmartTableColumnConfig[], p?: { refresh?: boolean }): void {
        this.columnConfiguration = columnConfigs;

        if (this.table) {
            this.table.options.setWithoutRefresh({
                columnsConfig: [...this.columnConfiguration]
            });
        }
        // Refresh column configurator
        if (p?.refresh !== false) {
            this.refresh();
        }
    }

    /** Refresh */
    public override refresh(): void {
        let columnConfiguration = this.columnConfiguration;
        // Update columnConfiguration from table columnsConfig
        if (this.table?.options.columnsConfig) {
            columnConfiguration = this.table.options.columnsConfig;
        }

        this.columnConfiguration = columnConfiguration.sort((c1, c2) => compareFields(c1, c2, "order"));
        const columns = getVOFValue(this.columns) ?? [];
        const columnsConf = getColumnsFromConfiguration(columns, this.columnConfiguration);

        // Visible columns on the right side of the component
        this.components.visibleColumns.setData({ data: columnsConf.visible });

        // Refresh resetBtn display
        toggleVisible(this.components.resetBtn, this.options.defaultVisibleColumnUids != null, Display.INITIAL);

        // Non visible columns on the left side of the component
        if (this.options.sortComparator) {
            // sort columns
            columnsConf.nonVisible.sort((a, b) => this.options.sortComparator?.(this, a, b) ?? 0);
        }
        this.components.nonVisibleColumns.setData({ data: columnsConf.nonVisible });

        // Update selection in components smartList
        this._updateSelection(columnsConf.visible, columnsConf.nonVisible);

        // Reset enabled/disabled for buttons
        this._updateButtonsDisabled();

        // Refresh table when all configuration is compute
        if (!this.options.disableTableRefreshInConfiguratorRefresh) {
            this.table?.refresh(columnConfiguratorSmartTableRefreshReason);
        }

    }

    /** get column name from options or table */
    public getColumnName(col: SmartTableColumn<ItemData>, colHeaderElement?: HTMLTableCellElement): AcceptedHtmlElementContent | undefined {
        return this.options.getColumnName?.(col, colHeaderElement) ?? this.table?.getColumnTitle(col, colHeaderElement);
    }

    /** Init smartList */
    protected _initSmartLists(): void {

        // Init smartList options, display propertyConfig.label, should display title
        const columnEditionOptions: Partial<SmartTableOptions<SmartTableColumn<ItemData>>> = {
            getRowUid: (item) => item.uid,
            renderCell: (cell: SmartTableCell<SmartTableColumn<ItemData>>): AcceptedHtmlElementContent => {
                return new CjsEllipsis(cell.value, {}, { elementCssClasses: [H_100, W_100, PADDING.PS_1, TEXT_TRUNCATE] });
            },
            selection: {
                enableBackgroundDisplay: true,
                selectionType: SELECTION_TYPE.MULTIPLE_INTERVAL
            },
            columns: [{
                uid: SINGLE_COLUMN_UID,
                getCellValue: (cell: SmartTableCellGetValue<SmartTableColumn<ItemData>>) => {
                    return this.getColumnName(cell.rowItemData as SmartTableColumn<ItemData>, createHtmlElement("th"));
                },
                getDisplayString: cell => cell.value
            }],
            parentContainerHasNoHeightConstraint: false,
            // Display header to display filter
            disableHeader: false,
            disableSort: true,
            disableTitle: true,
            disableFilter: false
        };

        // Set options
        this.components.nonVisibleColumns.options.setWithoutRefresh(columnEditionOptions);
        this.components.visibleColumns.options.setWithoutRefresh(columnEditionOptions);

        // update buttons enabled/disabled when columns selected
        this.components.nonVisibleColumns.rowClickEvent.on(e => this._updateButtonsDisabled(e.detail.cell));
        this.components.visibleColumns.rowClickEvent.on(e => this._updateButtonsDisabled(e.detail.cell));

        this.components.nonVisibleColumnsLabel.innerHTML = this.i18n.columnsLabel.nonVisibleColumn;
        this.components.visibleColumnsLabel.innerHTML = this.i18n.columnsLabel.visibleColumn;
    }

    /** Init Buttons */
    protected _initButtons(): void {

        const c = this.components;

        /** Init buttons */
        this._setBtnTooltipTitle(c.moveRightBtn, this.i18n.addTooltip);
        c.moveRightBtn.clickEvent.on(_ => this._moveSide("right"));

        this._setBtnTooltipTitle(c.moveLeftBtn, this.i18n.removeTooltip);
        c.moveLeftBtn.clickEvent.on(_ => this._moveSide("left"));

        this._setBtnTooltipTitle(c.moveUpBtn, this.i18n.upTooltip);
        c.moveUpBtn.clickEvent.on(_ => this._moveVertical("up", false));

        this._setBtnTooltipTitle(c.moveDownBtn, this.i18n.downTooltip);
        c.moveDownBtn.clickEvent.on(_ => this._moveVertical("down", false));

        this._setBtnTooltipTitle(c.allBtn, this.i18n.allTooltip);
        c.allBtn.clickEvent.on(_ => this.buildColumnConfig("all"));

        this._setBtnTooltipTitle(c.noneBtn, this.i18n.clearTooltip);
        c.noneBtn.clickEvent.on(_ => this.clearColumnConfig());

        this._setBtnTooltipTitle(c.moveOnTopBtn, this.i18n.topTooltip);
        c.moveOnTopBtn.clickEvent.on(_ => this._moveVertical("up", true));

        this._setBtnTooltipTitle(c.moveAtBottomBtn, this.i18n.bottomTooltip);
        c.moveAtBottomBtn.clickEvent.on(_ => this._moveVertical("down", true));

        this._setBtnTooltipTitle(c.resetBtn, this.i18n.resetTooltip);
        c.resetBtn.clickEvent.on(_ => this.buildColumnConfig("initial"));

        this._updateButtonsDisabled();
    }

    /** Enable/disable buttons with number of rows selected */
    protected _updateButtonsDisabled(cell?: SmartTableCell<SmartTableColumn<ItemData>>): void {

        const visibleRowsSelected = this.components.visibleColumns.getSelectedRows();
        const nonVisibleRowsSelected = this.components.nonVisibleColumns.getSelectedRows();

        // Can move left only if visible columns selected
        this.components.moveLeftBtn.options.setAndRefreshIfChanged({ disabled: !visibleRowsSelected.length || !this._columnCanBeHidden(cell) });

        // Can move right only if non visible column selected
        this.components.moveRightBtn.options.setAndRefreshIfChanged({ disabled: !nonVisibleRowsSelected.length });

        // Can move up and down only if visible columns selected
        this.components.moveUpBtn.options.setAndRefreshIfChanged({ disabled: !visibleRowsSelected.length });
        this.components.moveDownBtn.options.setAndRefreshIfChanged({ disabled: !visibleRowsSelected.length });
        this.components.moveOnTopBtn.options.setAndRefreshIfChanged({ disabled: !visibleRowsSelected.length });
        this.components.moveAtBottomBtn.options.setAndRefreshIfChanged({ disabled: !visibleRowsSelected.length });
    }


    /** Update selection */
    protected _updateSelection(visibleColumns: SmartTableColumn<ItemData>[], nonVisibleColumns: SmartTableColumn<ItemData>[]): void {
        if (this._leftSelection.length) {
            const nonVisibleColumnsSelectionModel = this.components.nonVisibleColumns.getSelectionModel();
            nonVisibleColumnsSelectionModel.clear();

            const rowsToSelect: RowUid[] = [];
            for (const row of nonVisibleColumns) {
                const rowUid = row.uid;
                if (this._leftSelection.includes(rowUid)) {
                    rowsToSelect.push(rowUid);
                }
            }
            this.components.nonVisibleColumns.selectRows(rowsToSelect, { preventScroll: true });

            // If not selection found, set selection to first row
            if (nonVisibleColumnsSelectionModel.isEmpty()) {
                const firstRowUidDisplayed = this.components.nonVisibleColumns.tableRows[0]?.uid;
                if (firstRowUidDisplayed) {
                    this.components.nonVisibleColumns.selectRows(firstRowUidDisplayed);
                }
            }

        }

        const visibleColumnsSelectionModel = this.components.visibleColumns.getSelectionModel();

        if (this._verticalSelection.length) {
            visibleColumnsSelectionModel.clear();
            const rowsToSelect: RowUid[] = [];
            for (const row of visibleColumns) {
                const rowUid = row.uid;
                if (this._verticalSelection.includes(rowUid)) {
                    rowsToSelect.push(rowUid);
                }
            }
            this.components.visibleColumns.selectRows(rowsToSelect, { preventScroll: true });
        } else if (this._rightSelection.length) {
            visibleColumnsSelectionModel.clear();

            const rowsToSelect: RowUid[] = [];
            for (const row of visibleColumns) {
                const rowUid = row.uid;
                if (this._rightSelection.includes(rowUid)) {
                    rowsToSelect.push(rowUid);
                }
            }
            this.components.visibleColumns.selectRows(rowsToSelect, { preventScroll: true });

            // If not selection found, set selection to first row
            if (visibleColumnsSelectionModel.isEmpty()) {
                const firstRowUidDisplayed = this.components.visibleColumns.tableRows[0]?.uid;
                if (firstRowUidDisplayed) {
                    this.components.visibleColumns.selectRows(firstRowUidDisplayed);
                }
            }
        }
    }


    /** Get selected columns from left to right or from right to left */
    protected _moveSide(side: "right" | "left"): void {
        const selectedColumns = side === "right" ? this.components.nonVisibleColumns.getSelectedRows() : this.components.visibleColumns.getSelectedRows();
        this._leftSelection = [];
        this._rightSelection = [];
        this._verticalSelection = [];

        // For each data update configuration : set visible/not visible and reset orders
        const updateConfig = updateVisibleColumnConfigurations(selectedColumns.map(c => getVOFValue(c.data)?.uid ?? ""), this.columnConfiguration);
        if (!updateConfig) {
            throw "Update config has failed";
        }

        // refresh selection
        for (const col of selectedColumns) {
            const columnData = getVOFValue(col.data);
            if (!columnData) { continue; }

            if (side === "right") {
                this._rightSelection.push(columnData.uid);
            } else if (!this.options.nonRemovableColumnUids || !this.options.nonRemovableColumnUids?.includes(col["uid"])) {
                this._leftSelection.push(columnData.uid);
            }
        }

        if (this.table) {
            this.table.options.setWithoutRefresh({
                columnsConfig: [...this.columnConfiguration]
            });
        }
        this.refresh();
        this.columnConfigurationHasChangeEvent.emit({ columnConfigs: this.columnConfiguration });
    }

    /** Move selected down in list */
    protected _moveVertical(vertical: "up" | "down", allTheWay: boolean): void {
        // Get all selected uids
        const selectedColumns = this.components.visibleColumns.getSelectedRows();
        const uids: ColumnUid[] = [];
        for (const col of selectedColumns) {
            const columnData = getVOFValue(col.data);
            if (!columnData) { continue; }
            uids.push(columnData.uid);
        }
        this._leftSelection = [];
        this._rightSelection = [];

        this._verticalSelection = uids;

        // Update order for selected Uids
        const updateConfig = updateOrderColumnConfiguration(uids, this.columnConfiguration, { up: vertical === "up", allTheWay });
        if (updateConfig) {
            if (this.table) {
                this.table.options.setWithoutRefresh({
                    columnsConfig: [...this.columnConfiguration]
                });
            }
            this.refresh();
            this.columnConfigurationHasChangeEvent.emit({ columnConfigs: this.columnConfiguration });
        } else {
            // TODO error ? event ? 
        }
    }

    /** Clear configuration in smartTable */
    public clearColumnConfig(): void {

        const columns = getVOFValue(this.columns) ?? [];
        const columnConfiguration = buildInitialColumnConfiguration(columns, this.options.defaultVisibleColumnUids);
        for (const col of columnConfiguration) {
            if (this.options.nonRemovableColumnUids?.includes(col.uid)) { continue; }
            col.visible = false;
        }
        this.columnConfiguration = columnConfiguration;

        if (this.table) {
            this.table.options.setWithoutRefresh({
                columnsConfig: this.columnConfiguration
            });
        }
        this.refresh();
        this.columnConfigurationHasChangeEvent.emit({ columnConfigs: this.columnConfiguration });
    }

    /** Build configuration from all columns smartTable */
    public buildColumnConfig(mode: "all" | "initial"): void {
        const columns = getVOFValue(this.columns) ?? [];
        this.columnConfiguration = buildInitialColumnConfiguration(columns, mode === "initial" ? this.options.defaultVisibleColumnUids : undefined);

        if (this.table) {
            this.table.options.setWithoutRefresh({
                columnsConfig: this.columnConfiguration
            });
        }
        this.refresh();
        this.columnConfigurationHasChangeEvent.emit({ columnConfigs: this.columnConfiguration });
    }

    /** Set tooltip of buttons */
    protected _setBtnTooltipTitle(button: CjsButton, label: string): void {
        button.options.setAndRefreshIfChanged({
            tooltip: { title: label, delay: this.BUTTONS_TOOLTIP_DELAY, placement: "right" }
        });
    }


    /** @returns true if column can be hidden */
    protected _columnCanBeHidden(cell?: SmartTableCell<SmartTableColumn<ItemData>>): boolean {
        const colUid = cell?.rowItemData?.["uid"] ?? "";

        let columnCanBeHide = true;
        if (cell?.column.uid != null && this.options.nonRemovableColumnUids && colUid) {
            columnCanBeHide = !this.options.nonRemovableColumnUids.includes(colUid);
        }

        return columnCanBeHide;
    }

    /** Show empty dialog */
    public showEmptyDialog(options?: Partial<DialogOptions>): CjsDialog {
        const dialog = new CjsDialog({
            title: this.i18n.emptyTitle,
            preConfig: DialogPreConfig.ALERT,
            ...options
        });
        dialog.addToBody(this.i18n.emptyWarning);
        return dialog;
    }

    /** Restore default columns */
    public restoreDefaultColumns(): void {
        if (this.savedConfig?.length) {
            this.columnConfiguration = this.savedConfig;
            this.savedConfig = undefined;
            if (this.table) {
                this.table.options.setAndRefreshIfChanged({ columnsConfig: this.columnConfiguration });
            }
            this.refresh();
            this.columnConfigurationHasChangeEvent.emit({ columnConfigs: this.columnConfiguration });
        } else {
            this.buildColumnConfig("all");
        }
    }

    /** Show empty dialog and restore default columns*/
    public showEmptyDialogAndRestoreDefaultColumns(): void {
        if (this.options.handleEmptyColumnsWithContextMenu !== false && this.columnConfiguration.filter(c => c.visible).length === 0) {
            this.showEmptyDialog();
            this.restoreDefaultColumns();
        }
    }
}